import { Layer } from '@deck.gl/core';
import { FuelUnit, NM_TO_M } from '@fe/core';
import { IFlightItem } from '../flights/types';
import { RangeType } from '../RangeType';
import { DeckLayerCreator } from './deck-layer-creator';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import { AnimatedScatterplotLayer } from './layers/scatterplot/animated-scatterplot-layer';
import { HidableScatterplotLayer } from './layers/scatterplot/hidable-scatterplot-layer';

export class DeckRings extends DeckLayerCreator {
    constructor(plugin: DeckOverlayPlugin) {
        super(plugin);
        this.getRingRadius = this.getRingRadius.bind(this);
    }

    createLayers(data: IFlightItem[]): Layer[] {
        if (!this.props?.flights.active.visible) {
            return [];
        }

        const { type, lineColor, color } = this.props.flights.active.rings;
        const ringLayerProps: Partial<HidableScatterplotLayer<IFlightItem>['props']> = {
            data,
            pickable: false,
            wrapLongitude: true,
            radiusUnits: type === RangeType.pixels ? 'pixels' : 'meters',
            lineWidthUnits: 'pixels',
            radiusScale: 1,
            stroked: !!lineColor,
            getLineColor: lineColor,
            getLineWidth: 1,
            getFillColor: color,
            getRadius: (d) => this.getRingRadius(d),
            getPosition: (d) => this.getFlightPosition(d),
            getIsHidden: (d) => this.getIsHidden(d),
            updateTriggers: {
                all: [this.flightsVersion],
                getPosition: [this.propsVersion],
                getIsHidden: this.getIsHiddenUpdateTrigger
            }
        };

        return [
            this.isCoastMode
                ? new AnimatedScatterplotLayer<IFlightItem>({
                      ...ringLayerProps,
                      id: 'animated-rings',
                      getId: (d) => d.id,
                      positionTexture: this.plugin.positionTexture
                  })
                : new HidableScatterplotLayer<IFlightItem>({
                      ...ringLayerProps,
                      id: 'rings'
                  })
        ];
    }

    getRingRadius(flight: IFlightItem): number {
        if (!this.props) {
            return 0;
        }

        const { range, type } = this.props.flights.active.rings;
        const { defaultParameters, aircraftTypeParameters } = this.props.aircraftParameters;
        const flightSpeed = this.getFlightSpeed(flight);

        // Time, custom speed currently unavailable. Current speed is used instead
        if (type === RangeType.time) {
            return (flightSpeed * range * NM_TO_M) / 60;
        }

        if (type === RangeType.timeCustomSpeed) {
            const customSpeed =
                (aircraftTypeParameters && aircraftTypeParameters.find((x) => x.acType === flight.acType)?.speed) ??
                defaultParameters?.speed;
            if (customSpeed == null) {
                return 0;
            }
            return (customSpeed * range * NM_TO_M) / 60;
        }

        if (type === RangeType.time389) {
            return (389 * range * NM_TO_M) / 60;
        }

        // Distance
        if (type === RangeType.distance) {
            return range * NM_TO_M;
        }

        // Distance, RLOS
        if (type === RangeType.distanceRLOS) {
            return 0.87 * Math.sqrt(2.0 * (this.getFlightAltitude(flight) ?? 0)) * NM_TO_M;
        }

        // FOB
        if (type === RangeType.distanceFobCurrentSpeed || type === RangeType.distanceFobCustomSpeed) {
            const aircraftParameters =
                (aircraftTypeParameters && aircraftTypeParameters.find((x) => x.acType === flight.acType)) ?? defaultParameters;

            const rate = aircraftParameters?.fuelBurningRate;
            const fob = getFob(flight);
            if (rate == null || fob == null) {
                return 0;
            }

            const reserve = aircraftParameters?.fuelReserve ?? 0;
            const endurance = (fob - reserve) / rate; // hours
            if (endurance <= 0) {
                return 0;
            }

            const speed = type === RangeType.distanceFobCurrentSpeed ? flightSpeed : aircraftParameters?.speed;
            if (speed == null) {
                return 0;
            }

            return speed * endurance * NM_TO_M;
        }

        // Pixels
        return range;
    }
}

function getFob(flight: IFlightItem) {
    const { fob } = flight;
    if (fob == null) {
        return null;
    }

    if (fob.unit === FuelUnit.WeighPounds) {
        // Pounds
        return fob.value;
    }

    if (fob.unit === FuelUnit.WeightKilograms) {
        // We need pounds
        return fob.value * 2.204622622;
    }

    // We don't convert volume units
    return null;
}
